import { formatInTimeZone, toZonedTime } from 'date-fns-tz'
import { AI_USER_ACTION, AI_USER_AVAILABILITY_MAX_DAYS, getAiUserAvailability } from '@/lib/api/ai-user/ai-user-api'
import { getConsultation, rescheduleConsultation } from '@/lib/api/consultations/consultation-api'
import { getExam, rescheduleExam } from '@/lib/api/exams/exam-api'
import { getOrder, rescheduleOrder } from '@/lib/api/orders/order-api'

/**
 * Data do atendimento (docs/ai-user.md, "Fase 3"). Em INTERMEDIATION quem liga para o
 * parceiro é o supervisor; quem grava é a IA, pela mesma rota do modal "Agendar" do
 * detalhe do atendimento, e depois ela confirma com o contato. Nas empresas com agenda
 * própria ela mesma procura a vaga na agenda do profissional (`appointment_find_slots`)
 * e grava o horário que o contato escolheu.
 */

export function isAppointmentScheduleAction(name: string): boolean {
    return name === AI_USER_ACTION.appointmentSetSchedule
}

export function isAppointmentFindSlotsAction(name: string): boolean {
    return name === AI_USER_ACTION.appointmentFindSlots
}

type Kind = 'EXAM' | 'CONSULTATION' | 'PROCEDURE'
const KINDS: readonly Kind[] = ['EXAM', 'CONSULTATION', 'PROCEDURE']
const KIND_LABEL: Record<Kind, string> = { EXAM: 'exame', CONSULTATION: 'consulta', PROCEDURE: 'procedimento' }

async function loadRecord(kind: Kind, id: number): Promise<any | null> {
    const response =
        kind === 'EXAM' ? await getExam(id) : kind === 'CONSULTATION' ? await getConsultation(id) : await getOrder(id)
    return response.success ? response.result : null
}

export async function executeAppointmentSchedule(
    args: Record<string, unknown>,
    context: { clientId: number | null },
): Promise<string> {
    const refuse = (message: string) => JSON.stringify({ ok: false, messages: [`Erro: ${message}`] })
    const kind = String(args.kind ?? '').toUpperCase() as Kind
    const id = Number(args.appointment_id)
    const date = String(args.date ?? '')
    const arrivalOrder = args.arrival_order === true
    const time = String(args.time ?? '')
    if (!KINDS.includes(kind)) return refuse('kind inválido: use EXAM, CONSULTATION ou PROCEDURE.')
    if (!Number.isInteger(id) || id <= 0) return refuse('appointment_id inválido.')
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return refuse('date no formato AAAA-MM-DD.')
    if (!arrivalOrder && !/^\d{2}:\d{2}$/.test(time)) {
        return refuse('Informe time (HH:MM) ou arrival_order=true quando for por ordem de chegada.')
    }

    // O atendimento tem de ser do contato desta conversa.
    const record = await loadRecord(kind, id)
    if (!record) return refuse(`Não achei o ${KIND_LABEL[kind]} #${id}.`)
    if (context.clientId == null || Number(record.clientId) !== context.clientId) {
        return refuse(`O ${KIND_LABEL[kind]} #${id} não é do contato desta conversa.`)
    }

    // Mesma montagem do modal: horário de Brasília; ordem de chegada zera a hora.
    const scheduledAt = toZonedTime(`${date}T${arrivalOrder ? '00:00' : time}`, 'America/Sao_Paulo').toISOString()
    // Mesma data já gravada: regravar seria uma "remarcação" para a mesma data, e a
    // remarcação manda ao paciente o aviso de data nova por WhatsApp e e-mail.
    if (
        record.scheduledAt &&
        new Date(record.scheduledAt).getTime() === new Date(scheduledAt).getTime() &&
        Boolean(record.arrivalOrder) === arrivalOrder
    ) {
        return JSON.stringify({
            ok: true,
            messages: ['Esta data já estava gravada no atendimento; nada mudou.'],
            appointment: { kind, id, date, time: arrivalOrder ? null : time, arrivalOrder },
        })
    }
    const payload = { scheduledAt, arrivalOrder, reason: record.scheduledAt ? 'Data combinada com o contato' : undefined }
    const response =
        kind === 'EXAM'
            ? await rescheduleExam(id, payload)
            : kind === 'CONSULTATION'
              ? await rescheduleConsultation(id, payload)
              : await rescheduleOrder(id, payload)
    if (!response.success) return refuse(response.error || 'Não consegui gravar a data.')
    return JSON.stringify({
        ok: true,
        messages: [],
        appointment: { kind, id, date, time: arrivalOrder ? null : time, arrivalOrder },
    })
}

export function describeAppointmentSchedule(args: Record<string, unknown>): string {
    const kind = String(args.kind ?? '').toUpperCase() as Kind
    const when = args.arrival_order === true ? `${args.date} por ordem de chegada` : `${args.date} às ${args.time}`
    return `Gravando a data do ${KIND_LABEL[kind] ?? 'atendimento'} #${args.appointment_id}: ${when}.`
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/
const BRAZIL_TIME_ZONE = 'America/Sao_Paulo'

function addDays(date: string, days: number): string {
    const day = new Date(`${date}T12:00:00Z`)
    day.setUTCDate(day.getUTCDate() + days)
    return day.toISOString().slice(0, 10)
}

const WEEKDAY_LABEL: Record<string, string> = {
    sunday: 'domingo',
    monday: 'segunda',
    tuesday: 'terça',
    wednesday: 'quarta',
    thursday: 'quinta',
    friday: 'sexta',
    saturday: 'sábado',
}

/** Vagas do profissional do atendimento numa janela (padrão: hoje e os 6 dias seguintes). */
export async function executeAppointmentFindSlots(
    args: Record<string, unknown>,
    context: { clientId: number | null },
): Promise<string> {
    const refuse = (message: string) => JSON.stringify({ ok: false, messages: [`Erro: ${message}`] })
    const kind = String(args.kind ?? '').toUpperCase() as Kind
    const id = Number(args.appointment_id)
    if (!KINDS.includes(kind)) return refuse('kind inválido: use EXAM, CONSULTATION ou PROCEDURE.')
    if (!Number.isInteger(id) || id <= 0) return refuse('appointment_id inválido.')

    const today = formatInTimeZone(new Date(), BRAZIL_TIME_ZONE, 'yyyy-MM-dd')
    const rawFrom = String(args.from ?? '')
    const from = DATE_ONLY.test(rawFrom) && rawFrom > today ? rawFrom : today
    const rawTo = String(args.to ?? '')
    const limit = addDays(from, AI_USER_AVAILABILITY_MAX_DAYS - 1)
    const to = DATE_ONLY.test(rawTo) && rawTo >= from ? (rawTo > limit ? limit : rawTo) : addDays(from, 6)

    const record = await loadRecord(kind, id)
    if (!record) return refuse(`Não achei o ${KIND_LABEL[kind]} #${id}.`)
    if (context.clientId == null || Number(record.clientId) !== context.clientId) {
        return refuse(`O ${KIND_LABEL[kind]} #${id} não é do contato desta conversa.`)
    }
    const professionalId = Number(record.professionalId)
    if (!Number.isInteger(professionalId) || professionalId <= 0) {
        return JSON.stringify({
            ok: true,
            bookable: false,
            messages: [
                `O ${KIND_LABEL[kind]} #${id} não tem profissional da casa: o horário não sai da nossa agenda. ` +
                    'Use ask_supervisor (partner_schedule) com o caso pronto.',
            ],
        })
    }

    const response = await getAiUserAvailability(professionalId, from, to)
    if (!response.success || !response.result) {
        return refuse(`não consegui ler a agenda (${response.error || 'sem resposta'}). Use ask_supervisor (partner_schedule).`)
    }
    const availability = response.result
    if (!availability.bookable) {
        const why =
            availability.reason === 'intermediation'
                ? 'o horário é combinado com o parceiro'
                : availability.reason === 'no_agenda'
                  ? `${availability.professional.name} não tem agenda no sistema`
                  : `a agenda de ${availability.professional.name} não tem dias e horários de atendimento configurados`
        return JSON.stringify({
            ok: true,
            bookable: false,
            messages: [`Não dá para marcar pela agenda: ${why}. Use ask_supervisor (partner_schedule) com o caso pronto.`],
        })
    }
    const days = availability.days
        .filter(day => day.free.length > 0)
        .map(day => ({ date: day.date, weekday: WEEKDAY_LABEL[day.weekday] ?? day.weekday, free: day.free }))
    return JSON.stringify({
        ok: true,
        bookable: true,
        professional: availability.professional.name,
        period: { from, to },
        arrivalOrder: availability.arrivalOrder,
        slotMinutes: availability.slotDuration,
        days,
        messages: days.length
            ? availability.arrivalOrder
                ? ['Agenda por ordem de chegada: o horário marcado é a referência de chegada, e há espera.']
                : []
            : ['Nenhum horário livre neste período. Procure no período seguinte (from/to) antes de dizer que não há vaga.'],
    })
}

export function describeAppointmentFindSlots(args: Record<string, unknown>): string {
    const kind = String(args.kind ?? '').toUpperCase() as Kind
    const period = args.from ? ` a partir de ${args.from}` : ''
    return `Procurando horário na agenda para o ${KIND_LABEL[kind] ?? 'atendimento'} #${args.appointment_id}${period}.`
}
